import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { formatMediumDate } from '../../lib/format.js';

class LedgerView extends BaseComponent {
    static properties = {
        _entries: { type: Array, state: true },
        _summary: { type: Object, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .summary-cards { display: flex; gap: 1.5rem; margin-bottom: 1.5rem; }
        .summary-card {
            border: 1px solid #eee; border-radius: 8px; padding: 1rem 1.5rem; min-width: 140px;
        }
        .summary-card .label { font-size: 0.75rem; color: #888; text-transform: uppercase; letter-spacing: 0.05em; }
        .summary-card .value { font-size: 1.5rem; font-weight: 600; }
        .summary-card .value.earned { color: #198754; }
        .summary-card .value.paid { color: #0d6efd; }
        .summary-card .value.balance { color: #333; }
        .status-badge { font-size: 0.75rem; }
    `;

    constructor() {
        super();
        this._entries = [];
        this._summary = null;
        this._loading = true;
        this._error = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._loadData();
    }

    async _loadData() {
        this._loading = true;
        this._error = null;
        try {
            const [entriesData, summaryData] = await Promise.all([
                api.get('/api/ledger'),
                api.get('/api/ledger/summary'),
            ]);
            this._entries = entriesData.entries || [];
            this._summary = summaryData;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _formatRate(cents) {
        if (cents == null) return '$0.00';
        return `$${(cents / 100).toFixed(2)}`;
    }

    render() {
        return html`
            <div class="page-header">
                <h4>Ledger</h4>
            </div>
            <p class="page-subtitle">Your earnings from completed commissions.</p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                ${
                    this._summary
                        ? html`
                    <div class="summary-cards">
                        <div class="summary-card">
                            <div class="label">Total Earned</div>
                            <div class="value earned">${this._formatRate(this._summary.total_earned)}</div>
                        </div>
                        <div class="summary-card">
                            <div class="label">Total Paid</div>
                            <div class="value paid">${this._formatRate(this._summary.total_paid)}</div>
                        </div>
                        <div class="summary-card">
                            <div class="label">Balance</div>
                            <div class="value balance">${this._formatRate(this._summary.balance)}</div>
                        </div>
                    </div>
                `
                        : nothing
                }

                ${
                    this._entries.length > 0
                        ? html`
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Commission</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._entries.map(
                                (e) => html`
                                <tr>
                                    <td>${e.commission_slug || e.commission_id}</td>
                                    <td class="fw-medium">${this._formatRate(e.amount_cents)}</td>
                                    <td>
                                        <span class="badge status-badge ${e.status === 'paid' ? 'bg-success' : 'bg-warning text-dark'}">
                                            ${e.status}
                                        </span>
                                    </td>
                                    <td class="text-muted">${formatMediumDate(e.created_at)}</td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted text-center py-3">No ledger entries yet.</p>`
                }
            `
            }
        `;
    }
}

customElements.define('ledger-view', LedgerView);
